<template>
  <div class="content">
    <md-card>
      <md-card-header class="md-card-header-icon md-card-header-blue">
        <div class="card-icon user-avatar">
          <img-with-fallback :src="account.avatar" alt="User picture" />
        </div>
        <h3 class="title">
          {{
          `${account.treatment} ${account.firstName} ${account.lastName} ${account.secondLastName}`
          }}
          ({{ account.username }})
        </h3>
      </md-card-header>
      <md-card-content class="md-layout md-alignment-space-between">
        <div class="md-layout-item md-size-40 md-xsmall-size-100">
          <md-chip class="md-caption md-success" :key="group.name" v-for="group in account.groups">
            <strong>Grupo:</strong>
            {{ group.name }}
          </md-chip>
        </div>
        <md-spacer />
        <md-select v-model="selectedGroup" v-show="$canView('auth', 'group')">
          <md-option
            v-for="g in groups"
            :key="g.name"
            :value="g.name"
          >{{ g.name }} | {{ g.permissions }}</md-option>
        </md-select>
        <md-button class="md-simple md-primary md-layout-item" @click="copyData">
          <md-icon md-2x>file_copy</md-icon>Copiar datos
        </md-button>
        <md-button
          class="md-simple md-layout-item md-secondary"
          :to="{
            name: 'Perfil de usuario',
          }"
        >
          <md-icon>person_pin</md-icon>Volver a Perfil
        </md-button>
      </md-card-content>
    </md-card>

    <catalog-table
      :tableData="allData"
      :tableHeaders="tableHeaders"
      :props-to-search="['name', 'verboseName', 'app', 'appVerboseName']"
      :page-size="1000"
      table-class="permissions-class"
      large-content
      hide-footer
      hidePageSize
    >
      <component
        slot="name-cell"
        slot-scope="{ item }"
        :is="!!item.permissions ? 'span' : 'strong'"
      >{{ item.name || item.verboseName }}</component>
      <template
        :slot="`${permission}-cell`"
        slot-scope="{ item }"
        v-for="permission in ['add', 'change', 'view', 'delete']"
      >
        <template v-if="!!item.app">
          <md-icon :class="getPermissionIcon(permission, item.app, item.name)">
            {{
            hasPermission(permission, item.app, item.name) ? "check" : "close"
            }}
          </md-icon>
        </template>
      </template>
      <template slot="others-cell" slot-scope="{ item }" v-if="!!item.permissions">
        <span v-for="p in item.others" style="display: block; text-align: left">
          <md-icon
            :class="
              !hasPermission(p.codename, item.app, item.name)
                ? 'text-danger text-bold'
                : 'text-success'
            "
          >
            {{
            hasPermission(p.codename, item.app, item.name) ? "check" : "close"
            }}
          </md-icon>
          <strong
            :class="
              !hasPermission(p.codename, item.app, item.name)
                ? 'text-danger text-bold'
                : 'text-success'
            "
          >{{ p.codename }}</strong>
          {{ p.name }}
        </span>
      </template>
    </catalog-table>
  </div>
</template>

<script>
import fetchGroupInfo from "@/services/account/fetchGroupInfo";
import fetchPermissionsInfo from "@/services/account/fetchPermissionsInfo";
import { CatalogTable } from "@/components/";
import { flatten } from "lodash";
import { copyToClipboard } from "@/utils";

export default {
  name: "UserPermissions",

  components: {
    CatalogTable,
  },

  props: {
    account: {
      type: Object,
      required: true,
      default: () => ({
        groups: [],
        avatar: null,
        treatment: null,
        firstName: null,
        lastName: null,
        secondLastName: null,
        username: null,
      }),
    },
  },

  data() {
    return {
      permissionsInfo: [],
      groupInfo: {
        name: "",
        permissions: null,
      },
      groups: [],
      selectedGroup: null,

      permissionHeaders: {
        view: "👁️ Ver",
        add: "➕ Crear",
        change: "📝 Cambiar",
        delete: "🗑 Borrar",
      },

      ACTION_ICONS: {
        add: "add",
        view: "visibility",
        change: "edit",
        delete: "close",
      },
    };
  },

  computed: {
    tableHeaders() {
      return {
        name: "Nombre",
        ...this.permissionHeaders,
        others: "Otros",
      };
    },

    selectedGroupData() {
      return this.groups.find(({ name }) => name === this.selectedGroup);
    },

    allData() {
      return flatten(
        this.permissionsInfo.map(({ models, ...appData }) => [
          appData,

          ...models.map((model) => ({
            ...model,
            app: appData.name,
            appVerboseName: appData.verboseName,
          })),
        ])
      );
    },
  },

  watch: {
    selectedGroup(newSelection) {
      this.groupInfo = {
        name: "",
        permissions: null,
      };

      if (newSelection) {
        this.doFetchGroupInfo(newSelection);
      }
    },

    account: {
      immediate: true,

      handler(account) {
        if (account && account.groups && account.groups.length) {
          this.selectedGroup = account.groups[0].name;
        } else {
          this.selectedGroup = null;
        }
      },
    },
  },

  mounted() {
    this.doFetchGroups();
    this.doFetchPermissionsInfo();
  },

  methods: {
    doFetchGroups() {
      return fetchGroupInfo().then((data) => {
        this.groups = data;
      });
    },

    doFetchPermissionsInfo() {
      return fetchPermissionsInfo().then((data) => {
        this.permissionsInfo = data;
      });
    },

    doFetchGroupInfo(group) {
      return fetchGroupInfo(group).then((data) => {
        this.groupInfo = data;
      });
    },

    hasPermission(permission, app, model) {
      const permissions = this.groupInfo.permissions;

      if (!permissions) {
        return;
      }

      const appObj = permissions[app];

      if (!appObj || !appObj[model]) {
        return false;
      }

      return appObj[model][permission] ? true : false;
    },

    getPermissionIcon(permission, app, model) {
      const value = this.hasPermission(permission, app, model);

      return value === false
        ? "text-danger text-bold"
        : value === true
        ? "text-success"
        : "";
    },

    dumpPermission(item, permissions) {
      const { name: nameString, app } = item;

      const permissionsString = (item.permissions ? permissions : [])
        .map((permission) =>
          this.hasPermission(permission, app, nameString) ? "Si" : "no"
        )
        .join("\t");

      const othersString = item.others
        ? item.others
            .map((permission) =>
              this.hasPermission(permission.codename, app, nameString)
                ? permission.codename
                : null
            )
            .filter((permission) => !!permission)
            .join(", ")
        : "";

      return `${nameString}\t${permissionsString}\t${othersString}`;
    },

    copyData() {
      const header = Object.values(this.tableHeaders).join("\t");

      const keys = Object.keys(this.tableHeaders);

      const permissions = Object.keys(this.permissionHeaders);

      const text = this.allData
        .map((item) => this.dumpPermission(item, permissions))
        .join("\n");

      return copyToClipboard(`${header}\n${text}`);
    },
  },
};
</script>

<style lang="scss" scoped>
.text-right {
  display: flex;
}

.user-avatar img {
  border-radius: 100%;
  max-width: 48px;
}
</style>



// WEBPACK FOOTER //
// src/pages/Dashboard/Pages/Permissions.vue